import React from "react";
import styled from "@emotion/styled";
import Text from "./Text";
import {
  text,
  surface,
  spacing,
  fontWeight,
  fontSize,
  lineHeight,
  mediaBelow,
} from "@atlas-int/tokens";

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ResearchFooterProps {
  /** URL for the Viz Hub icon image */
  vizHubIconSrc: string;
  /** URL for the Growth Lab logo image */
  glLogoSrc: string;
}

// ─── Styled components ──────────────────────────────────────────────────────

const Root = styled.div`
  width: 100%;
  border-top: 1px solid #ddd;
  margin-top: 2rem;
  padding-top: 2vh;
  min-height: 240px;
`;

const LocalH3 = styled(Text)`
  text-transform: uppercase;
  color: #777;
`;

const Ul = styled.ul`
  display: flex;
  line-height: 0.85;
  margin-top: ${spacing[10]};
  padding: 0px;

  ${mediaBelow("headerMobile")} {
    flex-direction: column;
  }
`;

const Li = styled.li`
  flex: 1;
  display: flex;
  align-items: center;

  & + li {
    margin-left: ${spacing[16]};
  }

  ${mediaBelow("headerMobile")} {
    & + li {
      margin-left: 0;
    }
  }
`;

const ResearchIcon = styled.div`
  flex: 2;
  margin-right: 6%;

  img {
    width: 100%;
  }
`;

const ResearchCitation = styled.a`
  flex: 5;
  display: block;
  font-weight: ${fontWeight.semibold};
  font-size: 0.9375rem;
  line-height: 1.4;
  color: #000000;

  &&:hover {
    text-decoration: none;
  }
`;

// ─── Component ──────────────────────────────────────────────────────────────

const ResearchFooter: React.FC<ResearchFooterProps> = ({
  vizHubIconSrc,
  glLogoSrc,
}) => {
  return (
    <Root>
      <LocalH3 variant="heading-3">Explore more Growth Lab work</LocalH3>
      <Ul>
        <Li>
          <ResearchIcon>
            <img src={vizHubIconSrc} />
          </ResearchIcon>
          <ResearchCitation
            href="https://growthlab.app/"
            target="_blank"
            rel="noopener"
          >
            <Text variant="heading-4">
              Visit the Viz Hub, the Growth Lab's portfolio of award-winning,
              interactive visualizations powered by our research and insights.
            </Text>
          </ResearchCitation>
        </Li>
        <Li>
          <ResearchIcon>
            <img src={glLogoSrc} />
          </ResearchIcon>
          <ResearchCitation
            href="https://growthlab.hks.harvard.edu/"
            target="_blank"
            rel="noopener"
          >
            <Text variant="heading-4">
              Read the Growth Lab's latest published research covering a range
              of academic papers, books and policy pieces.
            </Text>
          </ResearchCitation>
        </Li>
      </Ul>
    </Root>
  );
};

export default ResearchFooter;
